import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, Link as LinkIcon, Download, ExternalLink, CheckCircle, AlertCircle, Clock, RefreshCw } from "lucide-react";
import { KnowledgeItem } from "@/hooks/knowledge/useKnowledgeItems";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface KnowledgeItemDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: KnowledgeItem;
}

const KnowledgeItemDetailDialog = ({ open, onOpenChange, item }: KnowledgeItemDetailDialogProps) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Unknown date";
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  const getFileSize = (sizeInBytes: number | null) => {
    if (sizeInBytes === null) return "Unknown size";
    const kb = sizeInBytes / 1024;
    if (kb < 1024) {
      return `${kb.toFixed(1)} KB`;
    }
    const mb = kb / 1024;
    return `${mb.toFixed(1)} MB`;
  };

  const downloadFile = async () => {
    if (item.type !== "file" || !item.file_path) return;

    try {
      const { data, error } = await supabase.storage
        .from("knowledge_files")
        .download(item.file_path);

      if (error) {
        throw error;
      }

      // Create a download link
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.file_name || "download";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const renderIcon = () => {
    switch (item.type) {
      case "snippet":
        return <FileText className="h-5 w-5 text-[#3a3e5d]" />;
      case "file":
        return <FileText className="h-5 w-5 text-green-500" />;
      case "link":
        return <LinkIcon className="h-5 w-5 text-purple-500" />;
      default:
        return <FileText className="h-5 w-5" />;
    }
  };

  const renderContent = () => {
    switch (item.type) {
      case "snippet":
        return (
          <div className="mt-4 prose max-w-none">
            <p className="whitespace-pre-wrap">{item.description || item.content}</p>
          </div>
        );
      case "file":
        return (
          <div className="mt-4">
            <div className="bg-muted p-4 rounded-md flex items-center justify-between">
              <div>
                <p className="font-medium">{item.file_name}</p>
                <p className="text-sm text-muted-foreground">
                  {item.file_type} • {getFileSize(item.file_size)}
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadFile}>
                <Download className="h-4 w-4 mr-2" />
                Download
              </Button>
            </div>
            {item.description && (
              <div className="mt-4">
                <h3 className="text-sm font-medium mb-2">Description</h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{item.description}</p>
              </div>
            )}
          </div>
        );
      case "link":
        return (
          <div className="mt-4 space-y-4">
            <div className="bg-muted p-4 rounded-md">
              <div className="flex items-center justify-between mb-2">
                <div className="truncate flex-1">
                  <p className="text-sm font-medium truncate">{item.url}</p>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <a href={item.url || "#"} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Visit
                  </a>
                </Button>
              </div>
              
              {/* Crawl status */}
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {item.ai_processed ? (
                  <>
                    <Badge variant="outline" className="gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Content Indexed
                    </Badge>
                    {item.updated_at && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Last crawled {formatDate(item.updated_at)}
                      </span>
                    )}
                    {/* Future recrawl button placeholder */}
                    {/* <Button variant="ghost" size="sm" disabled>
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Refresh Content
                    </Button> */}
                  </>
                ) : (
                  <Badge variant="outline" className="gap-1 text-muted-foreground">
                    <AlertCircle className="h-3 w-3" />
                    Not Indexed
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Description and crawled content */}
            {(item.description || (item.ai_processed && item.content)) && (
              <Tabs defaultValue={item.description ? "description" : "content"} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  {item.description && (
                    <TabsTrigger value="description">Description</TabsTrigger>
                  )}
                  {item.ai_processed && item.content && (
                    <TabsTrigger value="content">Crawled Content</TabsTrigger>
                  )}
                </TabsList>
                
                {item.description && (
                  <TabsContent value="description">
                    <div className="mt-2">
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">{item.description}</p>
                    </div>
                  </TabsContent>
                )}
                
                {item.ai_processed && item.content && (
                  <TabsContent value="content">
                    <div className="mt-2 max-h-[400px] overflow-y-auto">
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap font-mono bg-muted p-4 rounded-md">
                        {item.content}
                      </p>
                    </div>
                  </TabsContent>
                )}
              </Tabs>
            )}
            
            {!item.description && !(item.ai_processed && item.content) && (
              <p className="text-sm text-muted-foreground italic">No description or content available</p>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {renderIcon()}
            <DialogTitle>{item.title}</DialogTitle>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Added {formatDate(item.created_at)}
          </p>
        </DialogHeader>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default KnowledgeItemDetailDialog;
